import { useState, useMemo, useCallback } from 'react';
import type { Appointment } from '@/mocks/appointments';
import { staffMembers } from '@/mocks/appointments';

interface DailyCalendarProps {
  date: string;
  appointments: Appointment[];
  onSlotClick: (date: string, time: string, staff?: string) => void;
  onAppointmentClick: (appt: Appointment) => void;
  onAppointmentUpdate?: (appt: Appointment) => void;
}

const START_HOUR = 8;
const END_HOUR = 24;
const SLOT_MINUTES = 10;
const SLOT_HEIGHT = 36;

function getMinutesFromStart(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m - START_HOUR * 60;
}

function getStatusBlockColor(status: Appointment['status'], payment: Appointment['paymentStatus']): { bg: string; border: string } {
  if (status === 'İptal Etti' || status === 'Gelmedi') {
    return { bg: '#EF444420', border: '#EF4444' };
  }
  if (payment === 'Ödendi') {
    return { bg: '#10B98120', border: '#10B981' };
  }
  if (payment === 'Açık Ödeme') {
    return { bg: '#F59E0B20', border: '#F59E0B' };
  }
  if (payment === 'Eksik Ödeme') {
    return { bg: '#3B82F620', border: '#3B82F6' };
  }
  return { bg: '#C9A96220', border: '#C9A962' };
}

function getPaymentLabel(payment: Appointment['paymentStatus']): string {
  switch (payment) {
    case 'Ödendi': return 'NAKIT';
    case 'Açık Ödeme': return 'AÇIK';
    case 'Eksik Ödeme': return 'EKSİK';
    default: return 'AÇIK';
  }
}

function formatTimeRange(time: string, duration: number): string {
  const [h, m] = time.split(':').map(Number);
  const startMin = h * 60 + m;
  const endMin = startMin + duration;
  const endH = Math.floor(endMin / 60);
  const endM = endMin % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')} - ${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
}

function minutesToTime(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export default function DailyCalendar({ date, appointments, onSlotClick, onAppointmentClick, onAppointmentUpdate }: DailyCalendarProps) {
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<{ staff: string; time: string } | null>(null);

  const dayAppts = useMemo(() => {
    return appointments
      .filter(a => a.date === date)
      .sort((a, b) => a.time.localeCompare(b.time));
  }, [appointments, date]);

  const dateObj = useMemo(() => new Date(date + 'T00:00:00'), [date]);
  const dayName = dateObj.toLocaleDateString('tr-TR', { weekday: 'long' });
  const dayDate = dateObj.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });

  const totalSlots = ((END_HOUR - START_HOUR) * 60) / SLOT_MINUTES;
  const totalHeight = totalSlots * SLOT_HEIGHT;

  const timeSlots = useMemo(() => {
    const slots: { label: string; minute: number }[] = [];
    for (let h = START_HOUR; h < END_HOUR; h++) {
      for (let m = 0; m < 60; m += SLOT_MINUTES) {
        slots.push({
          label: `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`,
          minute: m,
        });
      }
    }
    return slots;
  }, []);

  const apptsByStaff = useMemo(() => {
    const map: Record<string, Appointment[]> = {};
    staffMembers.forEach(s => { map[s] = []; });
    dayAppts.forEach(appt => {
      if (map[appt.staff]) {
        map[appt.staff].push(appt);
      }
    });
    return map;
  }, [dayAppts]);

  const handleDragStart = useCallback((e: React.DragEvent, appt: Appointment) => {
    setDraggingId(appt.id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', appt.id);
    // Create custom drag image
    const el = e.currentTarget as HTMLElement;
    if (el) {
      e.dataTransfer.setDragImage(el, 10, 10);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, staff: string, time: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTarget({ staff, time });
  }, []);

  const handleDragLeave = useCallback(() => {
    setDropTarget(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, staff: string, time: string) => {
    e.preventDefault();
    const apptId = e.dataTransfer.getData('text/plain');
    if (!apptId || !onAppointmentUpdate) {
      setDraggingId(null);
      setDropTarget(null);
      return;
    }

    const appt = dayAppts.find(a => a.id === apptId);
    if (!appt) {
      setDraggingId(null);
      setDropTarget(null);
      return;
    }

    // Update time and staff
    const updated: Appointment = {
      ...appt,
      time,
      staff,
      // Sync serviceItems[0].staff so staff counts stay in sync
      serviceItems: appt.serviceItems?.length
        ? appt.serviceItems.map((si, i) => i === 0 ? { ...si, staff } : si)
        : undefined,
    };
    onAppointmentUpdate(updated);
    setDraggingId(null);
    setDropTarget(null);
  }, [dayAppts, onAppointmentUpdate]);

  const handleDragEnd = useCallback(() => {
    setDraggingId(null);
    setDropTarget(null);
  }, []);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <h2 className="text-lg font-semibold font-display" style={{ color: 'var(--text-primary)' }}>
          {dayName}
        </h2>
        <span className="text-sm" style={{ color: 'var(--text-muted)' }}>{dayDate}</span>
        <span className="ml-auto text-xs px-2 py-1 rounded-full" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
          {dayAppts.length} randevu
        </span>
      </div>

      <div className="spa-card overflow-hidden">
        <div className="overflow-x-auto">
          <div className="min-w-[900px]">
            {/* Staff header row */}
            <div className="flex border-b sticky top-0 z-10" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg)' }}>
              <div className="w-16 flex-shrink-0 border-r p-2 flex items-center justify-center">
                <span className="text-[10px] font-semibold" style={{ color: 'var(--text-muted)' }}>SAAT</span>
              </div>
              {staffMembers.map(staff => {
                const staffCount = (apptsByStaff[staff] || []).length;
                return (
                  <div
                    key={staff}
                    className="flex-1 p-2 text-center border-r text-xs font-semibold"
                    style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}
                  >
                    <div className="flex items-center justify-center gap-1">
                      {staff}
                      {staffCount > 0 && (
                        <span
                          className="inline-flex items-center justify-center w-4.5 h-4.5 min-w-[18px] rounded-full text-[9px] font-bold"
                          style={{ background: 'var(--gold)', color: '#FFFFFF' }}
                        >
                          {staffCount}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Drag hint */}
            {draggingId && (
              <div className="px-3 py-1.5 text-[10px] font-medium text-center" style={{ background: 'rgba(201,169,98,0.1)', color: 'var(--gold)' }}>
                <i className="ri-drag-move-line mr-1" /> Randevuyu istediğiniz saat ve terapist sütununa bırakın
              </div>
            )}

            {/* Time + staff grid */}
            <div className="flex relative" style={{ height: `${totalHeight}px` }}>
              {/* Time labels column */}
              <div className="w-16 flex-shrink-0 border-r relative" style={{ borderColor: 'var(--border-color)' }}>
                {timeSlots.map((slot, idx) => (
                  <div
                    key={slot.label}
                    className="absolute left-0 right-0 flex items-start justify-center text-[10px] font-medium pt-0.5 border-b"
                    style={{
                      top: `${idx * SLOT_HEIGHT}px`,
                      height: `${SLOT_HEIGHT}px`,
                      borderColor: slot.minute === 0 ? 'var(--border-color)' : 'rgba(0,0,0,0.03)',
                      color: slot.minute === 0 ? 'var(--text-secondary)' : 'var(--text-muted)',
                      background: slot.minute === 0 ? 'var(--bg-tertiary)' : undefined,
                    }}
                  >
                    {slot.minute === 0 ? slot.label : ''}
                  </div>
                ))}
              </div>

              {/* Staff columns */}
              {staffMembers.map(staff => {
                const staffAppts = apptsByStaff[staff] || [];
                return (
                  <div
                    key={staff}
                    className="flex-1 border-r relative"
                    style={{ borderColor: 'var(--border-color)' }}
                  >
                    {/* Background grid lines with drop zones */}
                    {timeSlots.map((slot, idx) => {
                      const isDropTarget = dropTarget?.staff === staff && dropTarget?.time === slot.label;
                      return (
                        <div
                          key={idx}
                          className="absolute left-0 right-0 border-b transition-colors"
                          style={{
                            top: `${idx * SLOT_HEIGHT}px`,
                            height: `${SLOT_HEIGHT}px`,
                            borderColor: slot.minute === 0 ? 'var(--border-color)' : 'rgba(0,0,0,0.03)',
                            background: isDropTarget
                              ? 'rgba(16,185,129,0.15)'
                              : slot.minute === 0
                                ? 'rgba(0,0,0,0.02)'
                                : undefined,
                            cursor: draggingId ? 'copy' : 'pointer',
                          }}
                          onClick={() => !draggingId && onSlotClick(date, slot.label, staff)}
                          onDragOver={(e) => draggingId && handleDragOver(e, staff, slot.label)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, staff, slot.label)}
                        />
                      );
                    })}

                    {/* Appointment blocks */}
                    {staffAppts.map(appt => {
                      const startMin = getMinutesFromStart(appt.time);
                      const slotIdx = startMin / SLOT_MINUTES;
                      const blockHeight = (appt.duration / SLOT_MINUTES) * SLOT_HEIGHT;
                      const colors = getStatusBlockColor(appt.status, appt.paymentStatus);
                      const phoneClean = appt.customerPhone.replace('+90 ', '').replace(/\s/g, '');
                      const isDragging = draggingId === appt.id;

                      return (
                        <div
                          key={appt.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, appt)}
                          onDragEnd={handleDragEnd}
                          onClick={(e) => { e.stopPropagation(); onAppointmentClick(appt); }}
                          className="absolute left-1 right-1 rounded-md p-1.5 text-left overflow-hidden transition-all border-l-[3px] flex flex-col cursor-grab active:cursor-grabbing select-none"
                          style={{
                            top: `${slotIdx * SLOT_HEIGHT + 1}px`,
                            height: `${Math.max(blockHeight - 2, 28)}px`,
                            background: colors.bg,
                            borderLeftColor: colors.border,
                            opacity: isDragging ? 0.6 : 1,
                            transform: isDragging ? 'scale(0.98)' : undefined,
                            zIndex: isDragging ? 50 : 10,
                          }}
                        >
                          <span className="text-[9px] font-bold leading-tight" style={{ color: colors.border }}>
                            {formatTimeRange(appt.time, appt.duration)}
                          </span>
                          <span className="text-[10px] font-bold mt-0.5 truncate leading-tight" style={{ color: 'var(--text-primary)' }}>
                            {appt.customerName.toUpperCase()}
                          </span>
                          {blockHeight > 36 && (
                            <span className="text-[9px] font-medium truncate leading-tight" style={{ color: 'var(--text-secondary)' }}>
                              {appt.service}
                            </span>
                          )}
                          {appt.packageName && blockHeight > 48 && (
                            <span className="text-[8px] font-semibold truncate leading-tight" style={{ color: '#C9A962' }}>
                              <i className="ri-gift-line mr-0.5" />{appt.packageName}
                            </span>
                          )}
                          {blockHeight > 52 && (
                            <span className="text-[9px] truncate leading-tight" style={{ color: 'var(--text-muted)' }}>
                              {phoneClean}
                            </span>
                          )}
                          {blockHeight > 60 && (
                            <span className="text-[9px] font-bold mt-auto leading-tight" style={{ color: colors.border }}>
                              ₺{appt.price.toLocaleString('tr-TR')}
                            </span>
                          )}
                          {blockHeight > 76 && (
                            <span className="text-[8px] uppercase truncate leading-tight" style={{ color: colors.border, opacity: 0.8 }}>
                              {getPaymentLabel(appt.paymentStatus)}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}